/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

import React from 'react';
import i18n from './i18n/i18n';
import { useStore } from './stores/common';
import { usePrimitiveStore } from './stores/commonPrimitive';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { HookAPI } from 'antd/lib/modal/useModal';

/**
 * Shared "you have unsaved changes — save first?" gate for any action that
 * discards or replaces the current project (create/open/switch type, load an
 * example, sign out…). Centralizes the dirty check so every such exit path
 * prompts consistently instead of each one re-deciding whether to warn.
 *
 * - When the project is NOT dirty (`changed === false`), `proceed` runs
 *   immediately with no dialog.
 * - When it IS dirty, the confirm dialog offers Yes/No:
 *     - No  → `proceed()` (discard and continue).
 *     - Yes → by default, request a save (flip `saveProjectFlag`) and then run
 *       `proceed()`. Callers that must sequence the save BEFORE the follow-up
 *       action (e.g. "save current, then open THAT specific recent project")
 *       pass `onSave` to fully own the Yes branch — in that case `proceed` is
 *       only used for the No branch.
 *
 * `modal` is antd's `App.useApp().modal` instance; the dialog text follows the
 * active language.
 */
export const askToSaveThen = (modal: HookAPI, proceed: () => void, onSave?: () => void) => {
  const lang = { lng: useStore.getState().language };
  if (usePrimitiveStore.getState().changed) {
    modal.confirm({
      title: i18n.t('message.DoYouWantToSaveChanges', lang),
      icon: <ExclamationCircleOutlined />,
      onOk: () => {
        if (onSave) {
          onSave();
        } else {
          usePrimitiveStore.getState().set((state) => {
            state.saveProjectFlag = true;
          });
          proceed();
        }
      },
      onCancel: () => proceed(),
      okText: i18n.t('word.Yes', lang),
      cancelText: i18n.t('word.No', lang),
    });
  } else {
    proceed();
  }
};
